import {mxResources} from '../mxGraphFactory';

export default class GraphParallelJoinResolver {
    constructor(name, steps, routes, model) {
        this.name = name;
        this.steps = steps;
        this.routes = routes;
        this.model = model;
        this.joins = new Map();
    }

    resolve(startStepIndex, validationContext) {
        this.joins.clear();
        this.steps.forEach(step => { step.value.joinTask = null; });
        const splits = this.steps.filter(step => step.value.splitType === 'And');
        for (const split of splits) {
            const joins = [...this.findJoins(split)];
            if (joins.length > 1) {
                validationContext.addStepError({
                    message: mxResources.get('taskAmbiguousSplitJoin', [
                        split.value.name, joins[0].value.name, joins[1].value.name
                    ]),
                    graphName: this.name,
                    stepId: split.id,
                    stepName: split.value.name
                });
                return;
            }
            split.value.joinTask = joins[0]?.id || null;
            if (!split.value.joinTask) {
                validationContext.addStepError({
                    fields: {
                        takeRoutesOf: mxResources.get('taskNoCollectorTask', [split.value.name])
                    },
                    graphName: this.name,
                    stepId: split.id,
                    stepName: split.value.name
                });
            }
        }

        const start = this.steps[startStepIndex];
        const pending = [{step: start, scopes: []}];
        const seen = new Set();
        const incoming = new Map();
        for (let index = 0; index < pending.length; index++) {
            const {step, scopes} = pending[index];
            const key = JSON.stringify([step.id, scopes.map(scope => scope.id)]);
            if (seen.has(key)) continue;
            seen.add(key);
            const nextScopes = step.value.splitType === 'And' && !scopes.includes(step)
                ? [...scopes, step] : scopes;
            for (const route of this.model.getEdges(step, false, true, true)) {
                if (!route.target) continue;
                const owner = nextScopes.at(-1);
                const closesScope = owner?.value.joinTask === route.target.id;
                const targetScopes = closesScope ? nextScopes.slice(0, -1) : nextScopes;
                const arrivals = incoming.get(route.target.id) || [];
                arrivals.push({route, scopes: targetScopes, owner: closesScope ? owner : null});
                incoming.set(route.target.id, arrivals);
                pending.push({step: route.target, scopes: targetScopes});
            }
        }

        const errors = new Set();
        for (const arrivals of incoming.values()) {
            arrivals.sort((first, second) => this.routes.indexOf(first.route) - this.routes.indexOf(second.route));
            for (let index = 1; index < arrivals.length; index++) {
                const first = arrivals[0];
                const current = arrivals[index];
                const sameScopes = first.scopes.length === current.scopes.length &&
                    first.scopes.every((scope, scopeIndex) => scope === current.scopes[scopeIndex]);
                if ((!sameScopes || first.owner !== current.owner && !this.areExclusive(first.route, current.route, start)) &&
                        !errors.has(current.route.id)) {
                    errors.add(current.route.id);
                    validationContext.addRouteError({
                        message: mxResources.get('taskIncorrectlyNested', [
                            current.route.source.value.name, current.route.target.value.name
                        ]),
                        graphName: this.name,
                        routeId: current.route.id,
                        routeName: current.route.value.name
                    });
                }
            }
        }
    }

    findJoins(split, active = new Set()) {
        if (this.joins.has(split)) return this.joins.get(split);
        if (active.has(split)) return new Set();
        const nestedActive = new Set([...active, split]);
        const joins = new Set();
        const seen = new Set();
        const visit = (step, afterJoin = false) => {
            if (!step || seen.has(step)) return;
            seen.add(step);
            const nestedJoin = !afterJoin && step.value.joinType === 'And' && this.steps.some(nested =>
                nested.value.splitType === 'And' && !nestedActive.has(nested) &&
                this.canReach(split, nested, step) && this.findJoins(nested, nestedActive).has(step));
            if (!afterJoin && step.value.joinType === 'And' && !nestedJoin) {
                joins.add(step);
            } else if (step.value.splitType === 'And') {
                for (const join of this.findJoins(step, nestedActive)) visit(join, true);
            } else {
                this.model.getEdges(step, false, true, true).forEach(route => visit(route.target));
            }
        };
        this.model.getEdges(split, false, true, true).forEach(route => visit(route.target));
        this.joins.set(split, joins);
        return joins;
    }

    areExclusive(first, second, start) {
        return this.steps.some(decision => {
            const routes = this.model.getEdges(decision, false, true, true);
            if (decision.value.splitType === 'And' ||
                    !routes.some(route => route.isConditional?.() || route.value.routingType === 'Conditional')) {
                return false;
            }
            if (this.canReach(start, first.source, decision) || this.canReach(start, second.source, decision)) {
                return false;
            }
            const firstRoutes = routes.filter(route => route === first ||
                this.canReach(route.target, first.source, first.target));
            const secondRoutes = routes.filter(route => route === second ||
                this.canReach(route.target, second.source, second.target));
            return firstRoutes.length > 0 && secondRoutes.length > 0 &&
                firstRoutes.every(route => !secondRoutes.includes(route));
        });
    }

    canReach(start, target, boundary) {
        const pending = [start];
        const seen = new Set();
        for (let index = 0; index < pending.length; index++) {
            const step = pending[index];
            if (!step || step === boundary || seen.has(step)) continue;
            if (step === target) return true;
            seen.add(step);
            this.model.getEdges(step, false, true, true).forEach(route => pending.push(route.target));
        }
        return false;
    }
}
